import {FormControl} from '@angular/forms';
import {TemplatePart} from '../value-template';

/**
 * One line of a compose-field's ordered, blank-aware text composition
 * (the structured line repeater - a description assembled from a dozen or
 * more labelled source columns): a source column, rendered only when its
 * cell is non-empty for the row, preceded by a fixed label the operator
 * types once. A line that continues the line above puts its value after that
 * line's value instead of on a new line ("Lager: PLAN 2 0"), and takes
 * `alonePrefixControl` as its label on a row where nothing on the line
 * above has a value. A line that starts its own line can ask for a blank
 * line above it (`blankAboveControl`), so the composed text reads as
 * separate paragraphs. `id` is stable across a drag reorder (`track`ed by
 * it, not by array index).
 */
export interface ComposeLineRow {
  id: number;
  columnControl: FormControl<string | null>;
  prefixControl: FormControl<string>;
  continueControl: FormControl<boolean>;
  alonePrefixControl: FormControl<string>;
  blankAboveControl: FormControl<boolean>;
}

export function createComposeLineRow(
  id: number, column: string | null = null, prefix = '', continues = false, alonePrefix = '',
  blankAbove = false,
): ComposeLineRow {
  return {
    id,
    columnControl: new FormControl<string | null>(column),
    prefixControl: new FormControl<string>(prefix, {nonNullable: true}),
    continueControl: new FormControl<boolean>(continues, {nonNullable: true}),
    alonePrefixControl: new FormControl<string>(alonePrefix, {nonNullable: true}),
    blankAboveControl: new FormControl<boolean>(blankAbove, {nonNullable: true}),
  };
}

export function composeLineRowControls(row: ComposeLineRow): FormControl[] {
  return [
    row.columnControl, row.prefixControl, row.continueControl, row.alonePrefixControl, row.blankAboveControl,
  ];
}

/**
 * `rows` as `conditional` template parts, in row order, skipping a row
 * with no column picked (an added-but-not-yet-configured line). Each
 * line's own leading breaks are added here, not typed by the operator:
 * one `\n`, or two on a line that asks for a blank line above it.
 * `_compose_row_value` strips them back off whichever part is the first
 * to actually render for a given row, so the composed value never starts
 * with a blank line regardless of which line happens to have data first.
 * A line that continues the line above gets no `\n` on its `prefix`, and
 * always carries an `alone_prefix` - its alone label on a line of its
 * own, behind the breaks of the line it continues (a value standing
 * alone keeps that line's blank line), and those breaks alone when the
 * label is empty: on the wire, that key is what marks a part as
 * continuing. Its own `blankAboveControl` is not read.
 */
export function composeRowsToParts(rows: ComposeLineRow[]): TemplatePart[] {
  // The breaks of the line a continued row joins: a single one for a
  // continued row with no line above it.
  let lineBreaks = '\n';
  return rows
    .filter(row => !!row.columnControl.value)
    .map(row => {
      if (row.continueControl.value) {
        return {
          kind: 'conditional' as const,
          value: row.columnControl.value!,
          prefix: row.prefixControl.value,
          alone_prefix: `${lineBreaks}${row.alonePrefixControl.value}`,
        };
      }
      lineBreaks = row.blankAboveControl.value ? '\n\n' : '\n';
      return {
        kind: 'conditional' as const,
        value: row.columnControl.value!,
        prefix: `${lineBreaks}${row.prefixControl.value}`,
      };
    });
}

/** A saved `conditional` part back as a compose line - the inverse of
 * `composeRowsToParts`: a part with an `alone_prefix` is a continued line,
 * a `prefix` starting with two breaks is a line with a blank line above
 * it, and every leading break that function adds comes back off the
 * labels. */
export function composeLineRowFromPart(id: number, part: TemplatePart): ComposeLineRow {
  const stripBreaks = (text: string | undefined) => (text ?? '').replace(/^\n+/, '');
  if (part.alone_prefix !== undefined) {
    return createComposeLineRow(id, part.value, part.prefix ?? '', true, stripBreaks(part.alone_prefix));
  }
  const blankAbove = (part.prefix ?? '').startsWith('\n\n');
  return createComposeLineRow(id, part.value, stripBreaks(part.prefix), false, '', blankAbove);
}

/** Whether `parts` is what `composeRowsToParts` emits: `conditional` parts
 * only. A hand-typed template mixes `text` and `column` but never produces
 * a `conditional`, so such parts can only have come from compose mode. */
export function isComposeParts(parts: TemplatePart[]): boolean {
  return parts.length > 0 && parts.every(part => part.kind === 'conditional');
}

/** A column's first sample value, or `{column}` when it has none. */
function sampleOrPlaceholder(column: string, firstSample: (column: string) => string | undefined): string {
  return firstSample(column) || `{${column}}`;
}

/** A line's rendered preview - the same "first sample value, illustration
 * not a real row" convention the flat template preview uses, with a
 * `{column}` placeholder for a column that has no sample. A line that
 * continues the line above is shown as it renders standing alone, with
 * its alone label; `previewComposeResult` shows it joined to the line
 * above. `null` when no column is picked yet. */
export function previewComposeLine(
  row: ComposeLineRow, firstSample: (column: string) => string | undefined,
): string | null {
  const column = row.columnControl.value;
  if (!column) {
    return null;
  }
  const label = row.continueControl.value ? row.alonePrefixControl.value : row.prefixControl.value;
  return `${label}${sampleOrPlaceholder(column, firstSample)}`;
}

/** All of `rows` composed together as one value, laid out the way the
 * backend's `_compose_row_value` renders a `conditional` template when
 * every line has a value: one line per line with a column, a continued
 * line joined to the line above, an empty line above a line that asks for
 * one, the first without its leading breaks.
 * Every such line is shown - a column with no sample as its `{column}`
 * placeholder - so the operator sees where each line lands, where a real
 * row would skip the blank ones. A continued line first in the list has
 * no line above to join, so it shows with its alone label. `null` when no
 * line has a column yet (illustration, not a real row - see
 * `previewComposeLine`). */
export function previewComposeResult(
  rows: ComposeLineRow[], firstSample: (column: string) => string | undefined,
): string | null {
  const parts = composeRowsToParts(rows);
  if (!parts.length) {
    return null;
  }
  return parts
    .map((part, index) => {
      const prefix = index === 0 && part.alone_prefix !== undefined ? part.alone_prefix : part.prefix;
      return `${prefix}${sampleOrPlaceholder(part.value, firstSample)}`;
    })
    .join('')
    .replace(/^\n+/, '');
}
